import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Candidate hostnames to probe during web-based mDNS discovery.
 * Follows the pattern esp32-central-[alphanumeric].local.
 * Seeded at runtime via addCandidate() (BLE onboarding or cache restore).
 */
const MDNS_CANDIDATES: string[] = [];

interface HealthResponse {
  uuid?: string;
  deviceKey?: string;
  name?: string;
  model?: string;
  deviceModel?: string;
  version?: string;
  fwVersion?: string;
  status?: string;
  mac?: string;
  /** Stable mDNS hostname reported by the Central (e.g. "esp32-central-abc123") */
  mdns_hostname?: string;
}

export interface DiscoveredDevice {
  key: string;
  uuid: string | null;
  ip: string;
  name: string | null;
  model: string;
  version: string;
  status: string;
  mac: string | null;
  /** Full mDNS hostname (without .local) for reconnection */
  mdnsHostname: string | null;
}

@Injectable({ providedIn: 'root' })
export class DeviceDiscoveryService {
  private readonly http = inject(HttpClient);

  readonly devices = signal<DiscoveredDevice[]>([]);
  readonly scanning = signal(false);
  readonly lastScanAt = signal<number | null>(null);

  async discover(): Promise<DiscoveredDevice[]> {
    if (this.scanning()) {
      return this.devices();
    }

    this.scanning.set(true);

    try {
      const discovered = await this.discoverWithMdns();
      this.devices.set(discovered);
      this.lastScanAt.set(Date.now());
      return discovered;
    } finally {
      this.scanning.set(false);
    }
  }

  private async discoverWithMdns(): Promise<DiscoveredDevice[]> {
    // Probe all candidates in parallel and collect successful responses
    console.log('[DeviceDiscovery] Probing mDNS candidates:', MDNS_CANDIDATES);
    const results = await Promise.all(
      MDNS_CANDIDATES.map((host) => this.probe(host)),
    );
    const found = results.filter((d): d is DiscoveredDevice => d !== null);
    console.log('[DeviceDiscovery] Devices found:', found);
    return found;
  }

  /**
   * Probe a host by hitting /api/health.
   *
   * If the response contains `mdns_hostname`, the canonical hostname takes
   * priority over whatever candidate was probed.
   */
  private async probe(host: string): Promise<DiscoveredDevice | null> {
    try {
      const payload = await firstValueFrom(
        this.http.get<HealthResponse>(`http://${host}/api/health`, { responseType: 'json' }),
      );
      const uuid = payload.uuid ?? payload.deviceKey ?? null;
      const version = payload.version ?? payload.fwVersion ?? 'desconocida';
      const model = payload.model ?? payload.deviceModel ?? 'RadiantFloor';
      const status = payload.status ?? 'online';
      const mdnsHostname = payload.mdns_hostname ?? null;

      // Always prefer the canonical mDNS hostname reported by the device.
      const resolvedHost = mdnsHostname ? `${mdnsHostname}.local` : host;

      return {
        key: uuid ?? payload.mac?.toLowerCase() ?? host,
        uuid,
        ip: resolvedHost,
        name: payload.name ?? null,
        model,
        version,
        status,
        mac: payload.mac ?? null,
        mdnsHostname,
      };
    } catch {
      return null;
    }
  }

  /**
   * Add a candidate hostname (e.g. from a cached mdns_hostname) so
   * subsequent discover() calls probe it directly.
   */
  addCandidate(hostname: string): void {
    const host = hostname.endsWith('.local') ? hostname : `${hostname}.local`;
    if (!MDNS_CANDIDATES.includes(host)) {
      MDNS_CANDIDATES.push(host);
    }
  }
}
